/**
 * OAuth Login Page for ChatGPT Integration - v3
 * 
 * This page uses Stytch SDK's IdentityProvider component for proper
 * Connected Apps OAuth flow with ChatGPT.
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StytchProvider, useStytch } from '@stytch/react';
import { StytchUIClient } from '@stytch/vanilla-js';

// Stytch configuration
const STYTCH_PUBLIC_TOKEN = 'public-token-live-93829aa9-3610-423a-969e-12e3039a3b44';

// Create Stytch client
const stytchClient = new StytchUIClient(STYTCH_PUBLIC_TOKEN);

function OAuthContent() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'authenticating' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const stytch = useStytch();

  const initiateOAuth = useCallback(async () => {
    try {
      // Get OAuth parameters from URL
      const clientId = searchParams.get('client_id');
      const redirectUri = searchParams.get('redirect_uri');
      const state = searchParams.get('state');
      const codeChallenge = searchParams.get('code_challenge');
      const codeChallengeMethod = searchParams.get('code_challenge_method');
      const responseType = searchParams.get('response_type');
      const scope = searchParams.get('scope');
      const resource = searchParams.get('resource');

      console.log('[OAuth] Received params:', {
        clientId,
        redirectUri,
        state,
        responseType,
        scope,
      });

      if (!redirectUri || !state) {
        throw new Error('Missing required OAuth parameters');
      }

      // Store OAuth params in sessionStorage for after authentication
      sessionStorage.setItem('oauth_params', JSON.stringify({
        clientId,
        redirectUri,
        state,
        codeChallenge,
        codeChallengeMethod,
        responseType,
        scope,
        resource,
      }));

      setStatus('authenticating');

      // Start Google OAuth through Stytch
      // This will redirect to Google and back to our callback
      await stytch.oauth.google.start({
        login_redirect_url: `${window.location.origin}/oauth/callback`,
        signup_redirect_url: `${window.location.origin}/oauth/callback`,
      });

    } catch (error) {
      console.error('[OAuth] Error:', error);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to start authentication');
    }
  }, [searchParams, stytch]);

  useEffect(() => {
    // Small delay to ensure Stytch is ready
    const timer = setTimeout(() => {
      initiateOAuth();
    }, 100);
    return () => clearTimeout(timer);
  }, [initiateOAuth]);

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl max-w-md w-full text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-xl font-bold text-text-primary mb-2">Authentication Error</h1>
          <p className="text-text-secondary mb-6">{errorMessage}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      {/* Background gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-accent/20 rounded-full blur-3xl" />
      </div>

      <div className="relative bg-surface/80 backdrop-blur-xl border border-glass-border rounded-3xl p-8 shadow-2xl max-w-md w-full text-center">
        <img src="/logo.svg" alt="Vivideo" className="w-16 h-16 mx-auto mb-4 shadow-glow rounded-2xl" />
        <h1 className="text-xl font-bold text-text-primary mb-2">Connecting to Vivideo</h1>
        <p className="text-text-secondary mb-6">Redirecting to Google Sign-In...</p>
        
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

export function OAuthPage() {
  return (
    <StytchProvider stytch={stytchClient}>
      <OAuthContent />
    </StytchProvider>
  );
}

